import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { softwareApi, systemDocumentApi } from '../../api/systemRegisterApi';
import { formatEnumLabel } from '../../constants/enums';
import { apiErrorMessage, unwrapData } from '../../utils/responseHelpers';

const DetailRow = ({ label, value, fullWidth = false }) => (
  <TableRow>
    <TableCell
      sx={{
        width: fullWidth ? '22%' : '22%',
        fontWeight: 700,
        verticalAlign: 'top',
        color: 'text.secondary',
        borderBottomColor: 'divider',
      }}
    >
      {label}
    </TableCell>
    <TableCell
      sx={{
        verticalAlign: 'top',
        whiteSpace: 'pre-wrap',
        borderBottomColor: 'divider',
      }}
      colSpan={fullWidth ? 3 : 1}
    >
      {value === null || value === undefined || value === '' ? '—' : value}
    </TableCell>
  </TableRow>
);

const DocumentsDetailsViewPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryMain = theme.palette.primary.main;
  const [searchParams] = useSearchParams();
  const softwareId = searchParams.get('softwareId');
  const documentId = searchParams.get('id');

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!softwareId) {
        setError('No software selected.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const response = await softwareApi.get(softwareId);
        const data = unwrapData(response);
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, 'Failed to load document details.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [softwareId]);

  const documents = useMemo(() => {
    const list = detail?.documents ?? [];
    if (!documentId) return list;
    const selected = list.find((doc) => String(doc.id) === String(documentId));
    if (!selected) return list;
    return [selected, ...list.filter((doc) => String(doc.id) !== String(documentId))];
  }, [detail, documentId]);

  const handleDownload = async (doc) => {
    if (!doc?.location) {
      toast.error('No file available to download.');
      return;
    }
    setDownloadingId(doc.id ?? doc.fileName);
    try {
      const response = await systemDocumentApi.fetchFile(doc.location);
      const contentType =
        doc.contentType
        || response.headers?.['content-type']
        || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = doc.fileName || doc.documentName || 'document';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      toast.success('Download started.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to download document.'));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Box m={{ xs: '12px', sm: '20px' }}>
      <Header title="Documents" />

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={36} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : detail ? (
        <Box
          sx={{
            maxWidth: 980,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${alpha(primaryMain, isDark ? 0.35 : 0.16)}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                bgcolor: alpha(primaryMain, isDark ? 0.22 : 0.08),
                borderBottom: `1px solid ${alpha(primaryMain, isDark ? 0.35 : 0.12)}`,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                System details
              </Typography>
            </Box>
            <Table size="small">
              <TableBody>
                <DetailRow label="Software name" value={detail.softwareName} />
                <DetailRow label="Internal ID" value={detail.internalReference} />
                <DetailRow
                  label="Deployment status"
                  value={formatEnumLabel(detail.deploymentStatus) || detail.deploymentStatus}
                />
                <DetailRow label="Primary purpose" value={detail.primaryPurpose} fullWidth />
              </TableBody>
            </Table>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${alpha(primaryMain, isDark ? 0.35 : 0.16)}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                bgcolor: alpha(primaryMain, isDark ? 0.22 : 0.08),
                borderBottom: `1px solid ${alpha(primaryMain, isDark ? 0.35 : 0.12)}`,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Documents ({(detail.documents ?? []).length})
              </Typography>
            </Box>

            {documents.length === 0 ? (
              <Box px={2} py={2.5}>
                <Typography variant="body2" color="text.secondary">
                  No documents recorded for this system.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: alpha(primaryMain, isDark ? 0.12 : 0.04),
                        '& th': { fontWeight: 700, whiteSpace: 'nowrap' },
                      }}
                    >
                      <TableCell sx={{ minWidth: 180 }}>Document name</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>Deployment status</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Attached file</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Content type</TableCell>
                      <TableCell align="right" width={120}>
                        Download
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map((doc, index) => {
                      const selected = documentId && String(doc.id) === String(documentId);
                      const busy = downloadingId === (doc.id ?? doc.fileName);
                      return (
                        <TableRow
                          key={doc.id ?? `${doc.fileName}-${index}`}
                          hover
                          sx={{
                            bgcolor: selected
                              ? alpha(theme.palette.secondary.main, isDark ? 0.16 : 0.08)
                              : 'transparent',
                            borderLeft: selected
                              ? `3px solid ${theme.palette.secondary.main}`
                              : '3px solid transparent',
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={selected ? 700 : 500}>
                              {doc.documentName || '—'}
                            </Typography>
                            {selected ? (
                              <Typography variant="caption" color="secondary.main">
                                Selected
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatEnumLabel(doc.deploymentStatus) || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {doc.location && doc.fileName ? (
                              <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => handleDownload(doc)}
                                disabled={busy}
                                sx={{
                                  cursor: busy ? 'wait' : 'pointer',
                                  border: 'none',
                                  background: 'none',
                                  p: 0,
                                  font: 'inherit',
                                  color: 'primary.main',
                                  textAlign: 'left',
                                }}
                              >
                                {busy ? 'Downloading…' : doc.fileName}
                              </Link>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {doc.fileName || 'No file attached'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {doc.contentType || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {doc.location ? (
                              <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => handleDownload(doc)}
                                disabled={busy}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  cursor: busy ? 'wait' : 'pointer',
                                  border: 'none',
                                  background: 'none',
                                  p: 0,
                                  font: 'inherit',
                                  color: 'secondary.main',
                                  fontWeight: 600,
                                }}
                              >
                                {busy ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                                )}
                                {busy ? '…' : 'Download'}
                              </Link>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      ) : null}
    </Box>
  );
};

export default DocumentsDetailsViewPage;
